import React, { useEffect, useMemo } from "react";
import { TFunction, useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import Header from "go-app/components/Header";
import ImportCsv from "go-component/components/ImportCsv/ImportCsv";
import { ExportCSVTranslationsApi, ImportOverviewConfig } from "go-component/services/types";
import { useBrowserTabTitle } from "go-core/components/BrowserTab/useBrowserTabTitle";
import FormatDate from "go-core/components/Formatters/FormatDate";
import FormatMoney from "go-core/components/Formatters/FormatMoney";
import { selectOrganization } from "go-security/services/organizations/selectors";
import { OrderApi, OrderItemApi } from "../../../../../../../../../../services/Api/Organization/types";
import { getDefaultOrderTypesInputOptions, getOrderDateMatchTypesInputOptions } from "../../utils/utils";

const getTranslationsConfig = (t: TFunction): ExportCSVTranslationsApi => {
	return {
		export_csv: t("modules.order.action.export_csv.title"),
		resource_name: t("modules.order.field.resource_name.title"),
		problems_column_name: t("modules.order.action.number_of_orders.title"),
		constraints: {
			referenceid_cannot_be_empty: t("constraints.referenceid_cannot_be_empty"),
			tax_cannot_be_empty: t("constraints.tax_cannot_be_empty"),
			product_name_cannot_be_empty: t("constraints.product_name_cannot_be_empty"),
			order_number_cannot_be_empty: t("constraints.order_number_cannot_be_empty"),
			price_cannot_be_empty: t("constraints.price_cannot_be_empty"),
			quantity_cannot_be_empty: t("constraints.quantity_cannot_be_empty"),
			date_cannot_be_empty: t("constraints.date_cannot_be_empty"),
			date_cannot_be_set_to_the_future: t("constraints.date_cannot_be_set_to_the_future"),
			"quantity_must_be_between_0.001_and_999.999": t("constraints.quantity_must_be_between_0.001_and_999.999"),
			quantity_cannot_have_more_than_3_decimal_places: t(
				"constraints.quantity_cannot_have_more_than_3_decimal_places"
			),
			tax_cannot_be_set_to_a_decimal_value: t("constraints.tax_cannot_be_set_to_a_decimal_value"),
			tax_must_be_between_0_and_99: t("constraints.tax_must_be_between_0_and_99"),
			"price_cannot_be_greater_than_99999.99": t("constraints.price_cannot_be_greater_than_99999.99"),
			price_cannot_have_more_than_2_decimal_places: t("constraints.price_cannot_have_more_than_2_decimal_places"),
			wrong_order_type: t("constraints.wrong_order_type"),
			order_number_needs_to_be_the_same_for_all_items_with_the_same_reference_id: t(
				"constraints.order_number_needs_to_be_the_same_for_all_items_with_the_same_reference_id"
			),
			order_type_needs_to_be_the_same_for_all_items_with_the_same_reference_id: t(
				"constraints.order_type_needs_to_be_the_same_for_all_items_with_the_same_reference_id"
			),
			point_of_sale_needs_to_be_the_same_for_all_items_with_the_same_reference_id: t(
				"constraints.point_of_sale_needs_to_be_the_same_for_all_items_with_the_same_reference_id"
			),
		},
		database_column_name: {
			referenceId: t("enums.resource_type.order.reference_id"),
			orderNumber: t("enums.resource_type.order.order_number"),
			orderType: t("enums.resource_type.order.order_type"),
			date: t("enums.resource_type.order.date"),
			productName: t("enums.resource_type.order.product_name"),
			price: t("enums.resource_type.order.price"),
			quantity: t("enums.resource_type.order.quantity"),
			taxes: t("enums.resource_type.order.tax"),
			paymentMethods: t("enums.resource_type.order.payment_method"),
			pointsOfSale: t("enums.resource_type.order.point_of_sale"),
		},
	};
};

const OrganizationOrdersImportPage = (): JSX.Element => {
	const { t } = useTranslation();
	const organization = useSelector(selectOrganization);
	const { handleChangeTabTitle } = useBrowserTabTitle();

	useEffect(() => {
		handleChangeTabTitle(t("modules.order.header.orders_import.title"));
	}, []);

	const importOverviewConfig: ImportOverviewConfig = useMemo(
		() => ({
			formSelects: [
				{
					id: "default_order_type",
					options: getDefaultOrderTypesInputOptions(t),
					label: t("modules.order.field.default_order_type.title"),
					isRequired: true,
				},
				{
					id: "order_date_match_type",
					options: getOrderDateMatchTypesInputOptions(t),
					label: t("modules.order.field.order_date_match_type.title"),
					isRequired: true,
				},
			],
			dryImportProgressIncludes: "items,items.product,items.tax,transactions",
			previewItemsResponseKey: "updated",
			previewNestedItemsResponseKey: "items",
			previewItemsKeyName: "reference_id",
			previewNestedItemsRender: (subItem: OrderItemApi) => (
				<div className="d-flex align-items-center ps-3 sub-item-row">
					{`${subItem.product?.name} • ${`${t("common.word.quantity")} ${subItem.quantity}`} • ${`${t(
						"common.word.tax"
					)} ${subItem.tax?.name}`} • ${FormatMoney(subItem.total_price)}`}
				</div>
			),
			previewItemsColumns: [
				{
					id: "reference_id",
					name: t("modules.order.field.reference_id.title"),
				},
				{
					id: "number",
					name: t("modules.order.field.kitchen_order_number.title"),
				},
				{
					id: "type",
					name: t("modules.order.field.order_type.title"),
					render: (currentCellData: OrderApi) => t(`enums.orders.types.${currentCellData.type}`),
				},
				{
					id: "payment_method",
					name: t("common.word.payment_method"),
					render: (currentCellData: OrderApi) => {
						return currentCellData.transactions
							?.map((transaction) => transaction.payment_method?.name)
							.join(", ");
					},
				},
				{
					id: "created_at",
					name: t("modules.order.field.opened_at.title"),
					render: (currentCellData: OrderApi) =>
						FormatDate(currentCellData.created_at, undefined, undefined, {
							day: "2-digit",
							month: "2-digit",
							year: "numeric",
						}),
				},
				{
					id: "closed_at",
					name: t("modules.order.field.closed_at.title"),
					render: (currentCellData: OrderApi) =>
						FormatDate(currentCellData.closed_at, undefined, undefined, {
							day: "2-digit",
							month: "2-digit",
							year: "numeric",
						}),
				},
			],
			dryImportMappings: [
				{ id: "payment_methods" },
				{ id: "taxes" },
				{ id: "points_of_sale", hasSkipOption: true },
			],
		}),
		[t]
	);

	return (
		<>
			<Header title={t("modules.order.header.orders_import.title")} />
			<ImportCsv
				resourceType="orders"
				redirectionUrl={`/${organization.id}/sales/orders`}
				importCsvRequestParams=""
				translations={getTranslationsConfig(t)}
				workingInBackground
				omitExporting
				importOverviewConfig={importOverviewConfig}
			/>
		</>
	);
};
export default OrganizationOrdersImportPage;
